import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import yardStrugglingPlants from "@/assets/yard-struggling-plants.jpg";
import monarchMilkweed from "@/assets/monarch-milkweed.jpg";
import hummingbirdFeeding from "@/assets/hummingbird-native-flowers.jpg";
import layeredNativeGardenLayout from "@/assets/layered-native-garden-layout.jpg";

const CARDS = [
  {
    image: yardStrugglingPlants,
    alt: "Store-bought non-native plants struggling in a typical yard",
    title: "You're planting the wrong plants (without realizing it)",
    bullets: [
      "Garden centers sell what looks good — not what works",
      "Most plants aren't native to your area",
      "They struggle or need constant replacement",
    ],
    closing: "You keep spending money… and your yard never improves",
  },
  {
    image: monarchMilkweed,
    alt: "Monarch butterfly on native milkweed flowers",
    title: "Butterflies can't exist without the right plants",
    bullets: [
      "Monarchs need milkweed to reproduce",
      "Caterpillars rely on specific native plants",
      "Without them, butterflies disappear",
    ],
    closing: "No native plants = no butterflies",
  },
  {
    image: hummingbirdFeeding,
    alt: "Bird feeding from native plants in a healthy yard",
    title: "Birds disappear when insects disappear",
    bullets: [
      "Birds feed their young insects",
      "One nest needs thousands of caterpillars",
      "Insects only exist on native plants",
    ],
    closing: "Your yard may look alive… but it's actually silent",
  },
];

const TRANSITION_BULLETS = [
  "Built for your exact soil, sun, and climate",
  "Support bees, butterflies, and birds naturally",
  "Grow stronger each year with less guesswork",
];

const PRODUCT_BULLETS = [
  "Matches plants to your exact yard conditions",
  "Uses real ecological relationships (not generic lists)",
  "Shows exactly what to plant and where",
];

const WhyYardIsntThrivingSection = () => {
  return (
    <section className="py-14 md:py-20 bg-muted/30">
      <div className="container max-w-6xl">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14 max-w-3xl mx-auto">
          <h2 className="font-display text-2xl md:text-4xl font-bold leading-tight">
            Your Yard Might Look Green… But It's Not Supporting Life
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mt-4 leading-relaxed">
            Most yards don't support the insects and wildlife they need to survive — even if they look healthy.
          </p>
        </div>

        {/* 3 cards */}
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {CARDS.map(({ image, alt, title, bullets, closing }) => (
            <article
              key={title}
              className="flex flex-col rounded-2xl bg-card border border-border/40 shadow-soft overflow-hidden"
            >
              <div className="aspect-[4/3] overflow-hidden bg-muted">
                <img
                  src={image}
                  alt={alt}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col gap-4 p-5 md:p-6 flex-1">
                <h3 className="font-display font-bold text-base md:text-lg leading-snug">
                  {title}
                </h3>
                <ul className="space-y-2">
                  {bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2 text-sm text-foreground/80 leading-snug"
                    >
                      <span className="text-primary font-bold mt-0.5 shrink-0">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm font-semibold text-foreground italic mt-auto pt-2 border-t border-border/30">
                  {closing}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Solution Transition: Native Plants Bring Your Yard Back to Life */}
        <div className="mt-14 md:mt-20 grid md:grid-cols-2 gap-8 md:gap-10 items-center max-w-5xl mx-auto">
          <div className="rounded-2xl overflow-hidden border border-border/40 shadow-soft bg-card">
            <img
              src={layeredNativeGardenLayout}
              alt="Layered native garden bed alive with pollinators and birds"
              loading="lazy"
              width={1280}
              height={720}
              className="w-full h-auto block"
            />
          </div>
          <div>
            <h3 className="font-display text-xl md:text-2xl font-bold mb-4 leading-tight">
              Native Plants Bring Your Yard Back to Life
            </h3>
            <ul className="space-y-3 mb-8">
              {TRANSITION_BULLETS.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-2.5 text-sm md:text-base text-foreground"
                >
                  <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            {/* Product positioning */}
            <p className="text-base md:text-lg font-semibold text-foreground/90 mb-4 leading-snug">
              The problem isn't knowing native plants exist — it's knowing what works in YOUR yard.
            </p>
            <ul className="space-y-3 mb-5">
              {PRODUCT_BULLETS.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-2.5 text-sm md:text-base text-foreground"
                >
                  <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm md:text-base font-semibold text-primary italic">
              Stop guessing. Build a yard that actually works.
            </p>
          </div>
        </div>

        {/* Repeat primary CTA */}
        <div className="mt-10 md:mt-12 text-center">
          <Link to="/create" aria-label="Start my native plant plan">
            <Button variant="hero" size="xl" className="gap-2 min-h-[52px]">
              Start My Plan
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground mt-3">
            Takes less than 60 seconds. No experience needed.
          </p>
        </div>

        {/* Final microcopy */}
        <p className="mt-12 md:mt-14 text-center font-display text-lg md:text-2xl font-bold text-foreground">
          Build a yard that actually supports life.
        </p>
      </div>
    </section>
  );
};

export default WhyYardIsntThrivingSection;
